import {
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { upperFirst } from 'lodash-es';
import { Controller, useController, useFormContext } from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback } from 'react';
import { KPIRequestFrequency } from '../../../../../../data-models/kpi-requests.data-model';
import { FormField, Label } from '../../../../../../components/Form/FormComponents';
import { MONTHS } from '../../../Sections/FormItem/KPITable/useKPITableMetricsInputs';
import { ModalFormWidth, ZINDEX } from '../../../../../../constants/styles';
import { LabelInput } from '../../../../../../components/LabelInput/LabelInput';
import { ReminderThresholds } from '../../../../../../data-models/template.data-model';
import { emailArrayValidator } from './emailArrayValidator';

function FYESelect() {
  const { field } = useController({ name: 'fye' });

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      // send data to react hook form
      field.onChange(event.target.value);
    },
    [field]
  );

  return (
    <Select
      {...field}
      onChange={handleChange}
      MenuProps={{
        style: { zIndex: ZINDEX.MENU_INDEX },
      }}
    >
      {MONTHS.map((month, index) => (
        <MenuItem key={month} value={index + 1}>
          {month}
        </MenuItem>
      ))}
    </Select>
  );
}

function ReminderThresholdSelect() {
  const { field } = useController({ name: 'reminderThreshold' });
  const menuItems = ReminderThresholds.toArray((value, key) => {
    return (
      <MenuItem key={key} value={key}>
        {value}
      </MenuItem>
    );
  });

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      // send data to react hook form
      field.onChange(event.target.value);
    },
    [field]
  );

  return (
    <Select
      {...field}
      onChange={handleChange}
      MenuProps={{
        style: { zIndex: ZINDEX.MENU_INDEX },
      }}
    >
      {menuItems}
    </Select>
  );
}

export function KPIAssignRequestModalSettings() {
  const methods = useFormContext();
  const { colors } = useTheme();

  return (
    <Stack width={ModalFormWidth} gap='1rem' mt={'1rem'}>
      <Typography variant='body1'>Set Request Settings</Typography>
      <FormField>
        <Label required>Frequency</Label>
        <Controller
          name='frequency'
          control={methods.control}
          render={({ field }) => (
            <ToggleButtonGroup {...field} aria-label='select share with' color={'secondary'} exclusive>
              <ToggleButton value={KPIRequestFrequency.Monthly} aria-label='Frequency Monthly'>
                {upperFirst(KPIRequestFrequency.Monthly)}
              </ToggleButton>
              <ToggleButton value={KPIRequestFrequency.Quarterly} aria-label='Frequency Quarterly'>
                {upperFirst(KPIRequestFrequency.Quarterly)}
              </ToggleButton>
              <ToggleButton value={KPIRequestFrequency.Annual} aria-label='Frequency Annual'>
                {upperFirst(KPIRequestFrequency.Annual)}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
        <Stack
          direction={'row'}
          gap='.25rem'
          alignItems={'center'}
          p={'.5rem'}
          mt={'.25rem'}
          bgcolor={colors.primary[5]}
          borderRadius={'.25rem'}
        >
          <InfoOutlinedIcon sx={{ color: colors.neutral[40], fontSize: '1.25rem' }} />
          <Typography variant='caption' color={colors.neutral[70]}>
            The deadline will be set automatically for 2 weeks
          </Typography>
        </Stack>
      </FormField>
      <FormField>
        <Label required>FYE</Label>
        <FYESelect />
      </FormField>
      <Divider sx={{ mt: '.25rem' }} />
      <FormField>
        <Label required>Respondent</Label>
        <Controller
          name='respondent'
          control={methods.control}
          rules={{
            required: 'This field is required',
            validate: emailArrayValidator,
          }}
          render={({ field: { value, onChange }, fieldState }) => (
            <>
              <LabelInput
                fullWidth
                value={value}
                onChange={onChange}
                onCreateLabel={(label) => onChange([...value, label])}
                multiple
                required
                fieldPlaceholder='Respondent email'
                textFieldSx={{
                  marginLeft: 0,
                }}
                options={value.map((v: string) => ({ id: v, value: v, label: v }))}
                createPrompt='Add'
                errorText={fieldState.error ? fieldState.error.message : ''}
                disableCloseOnSelect={false}
                popupIcon={<ArrowDropDownIcon htmlColor='rgba(0, 0, 0, 0.54)' />}
                showMenuHelperText={false}
              />
            </>
          )}
        />
      </FormField>
      <Divider sx={{ mt: '.25rem' }} />
      <FormField>
        <Label required>Due Date</Label>
        <ReminderThresholdSelect />
      </FormField>
    </Stack>
  );
}
